import { NonRegulaDocumentUpload } from '@simplecitizen/gdl-regula-components';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hooks';
import { uploadNonIdentityDocument } from 'src/services/apiService';
import { addDoc } from 'src/store/slices/uploadedNonIdDocsSlice';

interface NonRegulaUploadCardProps {
  document: DocumentCollector;
  control: any;
  removeDocument: (
    document: DocumentCollector, 
    docToRemove: "nonIdentityDocuments" | "identityDocuments"
  ) => void;
}

interface DocumentSubmissionData {
  documentNumber: string;
  dateOfExpiry: string;
  issuingStateName: string;
  documentFile: File;
  backSideDocumentFile?: File;
}

interface DocumentDataItem {
  slug: string;
  name: { en: string };
  value: string;
  source: string;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://localhost:5003/api/workright";

const NonRegulaUploadCard = ({ document, control, removeDocument }: NonRegulaUploadCardProps) => {
  const { checkId } = useParams<{ checkId: string }>();
  const dispatch = useAppDispatch();
  
  // Redux selectors
  const selectedIDDocument = useAppSelector(
    (state: any) => state?.identity_doc_capture?.selectedIDDocument
  );
  const client = useAppSelector(
    (state) => state.questionnaire_data.questionnaire?.client
  );
  
  // Local state
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  
  // UI configuration
  const primaryColor = client?.branding?.primaryColor ?? "#14BDF3";
  
  /**
   * Handles file upload to the server
   */
  const handleFileUpload = async (file: File, key: string = "front"): Promise<any> => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("key", key);

      const config = {
        headers: {
          accept: "application/json",
          "Content-Type": "multipart/form-data",
        },
      };

      const { data } = await axios.post(
        `${API_BASE_URL}/v1/checks/${checkId}/document-collectors/${document?._id}/images`,
        formData,
        config
      );

      return data;
    } catch (error) {
      // Log file upload error to Sentry
      Sentry.captureException(error, {
        tags: {
          component: 'NonRegulaUploadCard',
          operation: 'handleFileUpload',
          documentType: document?.name?.en || 'unknown',
          documentId: document?._id || 'unknown'
        },
        extra: {
          checkId,
          fileKey: key,
          fileName: file?.name,
          fileSize: file?.size,
          fileType: file?.type
        }
      });
      
      throw new Error("An error occurred while uploading the file");
    }
  };
  
  /**
   * Creates document data structure for API submission
   */
  const createDocumentData = (
    documentNumber: string, 
    dateOfExpiry: string, 
    issuingStateName: string
  ): DocumentDataItem[] => {
    return [
      {
        slug: "document-number",
        name: { en: "Document Number" },
        value: documentNumber,
        source: "workright-employee-experience",
      },
      {
        slug: "date-of-expiry",
        name: { en: "Date of Expiry" },
        value: dateOfExpiry,
        source: "workright-employee-experience",
      },
      {
        slug: "issuing-state-name",
        name: { en: "Issuing State name" },
        value: issuingStateName,
        source: "workright-employee-experience",
      },
    ];
  };
  
  /**
   * Handles document submission with files and metadata
   */
  const submitNonIdDoc = async (data: DocumentSubmissionData) => {
    const {
      documentNumber,
      dateOfExpiry,
      issuingStateName,
      documentFile,
      backSideDocumentFile
    } = data;
    if (isSubmitting) return;
    
    try {
      setIsSubmitting(true);
      
      const imagesToSend = [];
      
      // Upload front side document if provided
      if (documentFile) {
        const frontImageData = await handleFileUpload(documentFile, "front");
        imagesToSend.push(frontImageData);
      }
      
      // Upload back side document if provided
      if (backSideDocumentFile) {
        const backImageData = await handleFileUpload(backSideDocumentFile, "back");
        imagesToSend.push({ format: "url", key: "back", source: backImageData?.source });
      }
      
      // Prepare document data
      const documentData = createDocumentData(documentNumber, dateOfExpiry, issuingStateName);
      
      // Submit to API
      const uploadedDocuments = await uploadNonIdentityDocument(
        document?._id!,
        checkId!,
        {
          type: selectedIDDocument?._type ?? null,
          data: documentData,
          images: imagesToSend,
        }
      );
      
      // Update Redux store with successful upload
      dispatch(
        addDoc({
          ...document,
          doc_key: uploadedDocuments._id,
          uploaded_image: imagesToSend[0]?.source,
        })
      );
      
    } catch (error: any) {
      // Log document submission error to Sentry
      Sentry.captureException(error, {
        tags: {
          component: 'NonRegulaUploadCard',
          operation: 'submitNonIdDoc',
          documentType: document?.name?.en || 'unknown',
          documentId: document?._id || 'unknown'
        },
        extra: {
          checkId,
          documentNumber,
          dateOfExpiry,
          issuingStateName,
          hasBackSideDocument: !!backSideDocumentFile,
          errorResponse: error?.response?.data,
          errorStatus: error?.response?.status,
          hasDocumentInError: !!error?.document
        }
      });
      
      // Handle case where document is returned despite error
      if (error?.document) {
        return dispatch(
          addDoc({
            ...document,
            doc_key: error?.document?._id ?? null,
            uploaded_image: error?.document?.images[0]?.source ?? null,
          })
        );
      }
      
      // Log other errors for debugging
      console.error("Error uploading document:", error);
      throw new Error("An error occurred while uploading the document. If this issue persists please contact workright@fragomen.com .");

    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div>
      <div className="mb-1 font-semibold">
        {document?.name.en || ""}{" "}
        <span className="">{document?.config?.required ? "*" : ""}</span>
      </div>
      
      <NonRegulaDocumentUpload
        maxFileSize={10}
        label={`${document.name.en || ""}`}
        documentName={document?.name?.en || ""}
        multipage={document?.config?.isMultipage ?? false}
        color={primaryColor}
        onRemove={() => removeDocument(document, "nonIdentityDocuments")}
        validators={document?.config?.validators}
        onSaveDocument={async (docData: DocumentSubmissionData) => {
          await submitNonIdDoc(docData);
        }}
      />
    </div>
  );
};

export default NonRegulaUploadCard;
